import { useQuota } from "@/application/queries/beneficiaries/quotaListQueries";
import { entityRefLink } from "@/utils/entityRefLink";
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentOrganizationId } from "@/application/workflows/organization";
import { InvalidationEntity, useRefreshEntities } from "@/application/queries";
import { useCommentActions } from "@/application/workflows/comments/useCommentActions";
import {
  useCommentPeople,
  useCommentSummary,
  useCommentThread,
  useCommentThreads,
} from "@/application/queries/comments/queries";
import type { CommentCommand } from "@/application/queries/comments/commentCommand";
import type { CommentScope } from "@/domain/comment";
import { useCommentsScopeStore } from "@/stores/useCommentsScopeStore";
import { useToastStore } from "@/stores/useToastStore";
import { useCommentsLabels } from "./useCommentsLabels";

export function useCommentsPanel(enabled: boolean) {
  const state = useCommentsScopeStore();
  const orgId = useCurrentOrganizationId();
  const { user } = useAuth();
  const labels = useCommentsLabels();
  const refresh = useRefreshEntities(orgId);
  const showToast = useToastStore((store) => store.showToast);
  const [params, setParams] = useSearchParams();
  const requested = params.get("thread");
  const requestedRecord = params.get("project") ?? params.get("peek") ?? params.get("open");
  const { pathname } = useLocation();
  const record = state.scope?.record;
  const subjectType = state.scope?.subjectType;
  const pageScope = !record && state.mode === "page" && !!subjectType;
  const scope: CommentScope =
    record && subjectType
      ? { subject_type: subjectType, subject_uuid: [record.uuid] }
      : pageScope
        ? { subject_type: subjectType, subject_uuid: state.scope?.visible.slice(0, 20) ?? [] }
        : {};
  const canQuery = enabled && (!pageScope || !!scope.subject_uuid?.length);
  const summary = useCommentSummary(orgId, scope, canQuery);
  const list = useCommentThreads(orgId, { ...scope, status: state.status }, canQuery && state.open);
  const resolved = useCommentThreads(
    orgId,
    { ...scope, status: "resolved" },
    canQuery && state.open,
  );
  const detail = useCommentThread(orgId, enabled && state.open ? state.thread : null);
  const parentQuota = useQuota(
    orgId,
    (detail.data?.subject.entity_type === "classification" ||
      detail.data?.subject.entity_type === "audit") &&
      detail.data.subject.parent?.entity_type === "fiscal_quota"
      ? detail.data.subject.parent.entity_uuid
      : null,
  );
  const subjectHref = detail.data
    ? entityRefLink(detail.data.subject, parentQuota.data?.beneficiary.uuid)
    : null;
  const people = useCommentPeople(orgId, enabled && state.open);
  const actor = user
    ? { uuid: user.uuid ?? user.id, name: user.name, avatar: user.avatar ?? null }
    : null;
  const actions = useCommentActions(orgId, actor);
  useEffect(() => {
    if (!enabled || !requested || state.scope?.page !== pathname) return;
    if (requestedRecord && record?.uuid !== requestedRecord) return;
    state.setThread(requested);
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete("thread");
        return next;
      },
      { replace: true },
    );
  }, [
    enabled,
    requested,
    requestedRecord,
    record?.uuid,
    pathname,
    state.scope?.page,
    state.setThread,
    setParams,
  ]);
  const readThread = detail.data?.uuid;
  useEffect(() => {
    if (readThread) refresh([InvalidationEntity.COMMENTS], { facets: ["lists", "aggregates"] });
  }, [refresh, readThread]);
  const close = () => {
    state.setOpen(false);
    if (requested)
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.delete("thread");
          return next;
        },
        { replace: true },
      );
  };
  const run = async (command: CommentCommand) => {
    const outcome = await actions.run(command);
    if (!outcome.ok) {
      showToast(labels.error, "error");
      return false;
    }
    if (command.action === "create" && outcome.value) state.setThread(outcome.value.uuid);
    if (command.action === "deleteThread" || command.action === "resolve") state.setThread(null);
    return true;
  };
  return {
    state,
    labels,
    actor,
    scope,
    subjectType,
    record,
    summary,
    list,
    resolved,
    detail,
    people,
    busy: actions.isPending,
    canQuery,
    run,
    close,
    subjectHref,
    canCreate: !!record && !!subjectType && enabled,
    canChooseScope: !record && !!subjectType,
  };
}
